import { useEffect, useMemo, useRef } from 'react'
import collapseZeros from '../../utils/collapseZeros';
import Blockchain from '../../models/Blockchain.enum';
import {
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  ResolutionString,
  Timezone,
  widget,
  type ThemeName,
  ResolveCallback,
  HistoryCallback,
  LibrarySymbolInfo,
  PeriodParams,
  ErrorCallback,
  SubscribeBarsCallback,
  ISymbolValueFormatter,
  CustomStudyFormatterFormat,
  CustomStudyFormatter,
  CustomFormatters,
} from '../../lib/charting-library';
import marketDataService from '../../services/MarketDataService';

import styles from './TradimgView.module.css';

interface Props {
  blockchain: Blockchain;
  tokenAddress: string | null;
  poolAddress: string | null;
  symbolName: string;
}

const supported_resolutions = [
  '1',
  '5',
  '15',
  '30',
  '60',
  '1D',
  '1W',
  '1M',
] as ResolutionString[];

const timezone = (Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone as Timezone) ?? 'Etc/UTC';

let timer: NodeJS.Timer;

export const TradingView = ({
  blockchain,
  tokenAddress,
  poolAddress,
  symbolName,
}: Props) => {
  const tradingViewContainerRef = useRef<HTMLDivElement | null>(null);

  const tradingViewWidgetRef = useRef<IChartingLibraryWidget | null>(null);

  const resolveSymbol = (symbolName: string, onResolve: ResolveCallback) => {
    onResolve({
      name: symbolName,
      description: '',
      type: 'crypto',
      timezone: 'Etc/UTC',
      session: '24x7',
      minmov: 1,
      exchange: '',
      listed_exchange: '',
      format: 'price',
      pricescale: 100,
      has_intraday: true,
      ticker: symbolName,
      supported_resolutions,
    });
  };

  async function getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: ErrorCallback,
  ) {
    const { from, to } = periodParams;

    const now = Math.round(Date.now() / 1000);

    try {
      const bars = await marketDataService.getKLines(
        blockchain,
        tokenAddress,
        poolAddress,
        resolution,
        from,
        to > now ? now : to,
      );

      onResult(bars, { noData: bars.length === 0 });
    } catch (error) {
      console.error(error);

      onError(JSON.stringify(error));
    }
  }

  const custom_formatters: CustomFormatters = {
    priceFormatterFactory: (
      symbolInfo: LibrarySymbolInfo | null,
      minTick: string
    ): ISymbolValueFormatter | null => ({
      format: (price: number, signPositive?: boolean): string => {
        return collapseZeros(price);
      },
    }),
      studyFormatterFactory: (
        format: CustomStudyFormatterFormat,
        symbolInfo: LibrarySymbolInfo | null,
        precision: string | undefined
      ): CustomStudyFormatter | null => {

      if (format.type === 'volume') {
        return ({
          format: (value: number | undefined) => collapseZeros(value),
        });
      }

      return null;
    },
  };

  async function subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    onResetCacheNeededCallback: () => void): Promise<void> {
    const fetchBars = async () => {
      const bars = await marketDataService.getKLines(
        blockchain,
        tokenAddress,
        poolAddress,
        resolution,
        undefined,
        undefined,
        1
      );

      if (bars?.length > 0) {
        onTick(bars[0]);
      }
    }

    timer = setInterval(fetchBars, process.env.REACT_APP_POLLING_INTERVAL);
  }

  const widgetOptions:
    | Omit<ChartingLibraryWidgetOptions, 'container'>
    | undefined = useMemo(
    () => ({
      symbol: symbolName,
      interval: '1' as ResolutionString,
      library_path: `/charting_library/`,
      fullscreen: false,
      autosize: true,
      locale: 'en',
      disabled_features: ['header_symbol_search'],
      theme: 'dark' as ThemeName,
      timezone,
      datafeed: {
        searchSymbols: () => {},
        resolveSymbol,
        getBars,
        subscribeBars,
        unsubscribeBars(listenerGuid: string) {
          if (timer) {
            clearInterval(timer);
          }
        },
        onReady: (callback) => {
          setTimeout(callback, 0, {})
        },
        getServerTime: async () => {
          return Date.now()
        },
      },
      custom_formatters,
    }),
    [getBars, subscribeBars, symbolName]
  );

  useEffect(() => {
    if (!tradingViewContainerRef.current || !widgetOptions) {
      return () => {};
    }

    tradingViewWidgetRef.current = new widget({
      ...widgetOptions,
      container: tradingViewContainerRef.current,
    })

    return () => {
      if (tradingViewWidgetRef.current !== null) {
        tradingViewWidgetRef.current.remove()
        tradingViewWidgetRef.current = null
      }
    }
  }, [widgetOptions])

  return (
    <div
      ref={tradingViewContainerRef}
      className={styles.container}
    />
  )
}

export default TradingView;
